/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import orderBy from 'lodash/orderBy';

import { getDashboardsV1API } from '../../Dashboard/services/DashboardAPIs';
import { getHierarchyDashboardsAPI } from '../../Setup/services/HierarchiesAPI';

import { getAssetResources } from 'AssetResourcesAPI';
import { getLocationResources } from 'LocationResourcesAPI';
import { getEntityTypesAPI } from 'EntityTypeAPI';
import { RESOURCE_TYPES } from 'dataConstants';
import { filterUserResourcesToObject } from 'Utils';

export const GET_MONITOR_ASSET_TYPES = 'GET_MONITOR_ASSET_TYPES';
export const GET_MONITOR_ASSET_TYPES_FAILURE = 'GET_MONITOR_ASSET_TYPES_FAILURE';
export const GET_MONITOR_ASSET_TYPES_SUCCESS = 'GET_MONITOR_ASSET_TYPES_SUCCESS';
export const GET_MONITOR_DASHBOARDS = 'GET_MONITOR_DASHBOARDS';
export const GET_MONITOR_DASHBOARDS_FAILURE = 'GET_MONITOR_DASHBOARDS_FAILURE';
export const GET_MONITOR_DASHBOARDS_SUCCESS = 'GET_MONITOR_DASHBOARDS_SUCCESS';

// HIERARCHIES DASHBOARDS_PROP_TYPES
export const GET_MONITOR_HIERARCHY_DASHBOARD = 'GET_MONITOR_HIERARCHY_DASHBOARD';
export const GET_MONITOR_HIERARCHY_DASHBOARD_FAILURE = 'GET_MONITOR_HIERARCHY_DASHBOARD_FAILURE';
export const GET_MONITOR_HIERARCHY_DASHBOARD_SUCCESS = 'GET_MONITOR_HIERARCHY_DASHBOARD_SUCCESS';

// Hierarchy list
export const PERSIST_HIERARCHY_LIST = 'PERSIST_HIERARCHY_LIST';
export const CLEAN_PERSIST_HIERARCHY_LIST = 'CLEAN_PERSIST_HIERARCHY_LIST';

// ASSETS
export const GET_MONITOR_ASSETS = 'GET_MONITOR_ASSETS';
export const GET_MONITOR_ASSETS_FAILURE = 'GET_MONITOR_ASSETS_FAILURE';
export const GET_MONITOR_ASSETS_SUCCESS = 'GET_MONITOR_ASSETS_SUCCESS';

// LOCATIONS
export const GET_MONITOR_LOCATIONS = 'GET_MONITOR_LOCATIONS';
export const GET_MONITOR_LOCATIONS_FAILURE = 'GET_MONITOR_LOCATIONS_FAILURE';
export const GET_MONITOR_LOCATIONS_SUCCESS = 'GET_MONITOR_LOCATIONS_SUCCESS';

const getMonitorAssetTypesStart = () => ({
  type: GET_MONITOR_ASSET_TYPES,
});

const getMonitorAssetTypesFailure = payload => ({
  type: GET_MONITOR_ASSET_TYPES_FAILURE,
  payload,
});

const getMonitorAssetTypesSuccess = payload => ({
  type: GET_MONITOR_ASSET_TYPES_SUCCESS,
  payload,
});

const getMonitorDashboardsStart = () => ({
  type: GET_MONITOR_DASHBOARDS,
});

const getMonitorDashboardsFailure = payload => ({
  type: GET_MONITOR_DASHBOARDS_FAILURE,
  payload,
});

const getMonitorDashboardsSuccess = payload => ({
  type: GET_MONITOR_DASHBOARDS_SUCCESS,
  payload,
});

// TODO: may be we can remove this ?
export const fetchMonitorAssetTypes = () => dispatch => {
  dispatch(getMonitorAssetTypesStart());
  return getEntityTypesAPI()
    .then(response => {
      const orderedAssetTypes = orderBy(response, ['name'], ['asc']);
      dispatch(getMonitorAssetTypesSuccess(orderedAssetTypes || []));
      return orderedAssetTypes || [];
    })
    .catch(error => {
      dispatch(getMonitorAssetTypesFailure(error));
      throw error;
    });
};

/**
 * return Device type or device level dashboards
 * @param {*} deviceTypeName Name of the device type
 * @param {*} deviceId Name of the device
 * @param {*} type Type of the dashboard. example instance dashboard (DEVICE) or Device type level dashboard (DEVICE_TYPE)
 * @returns
 */
export const fetchMonitorDashboards =
  (deviceTypeName, deviceId, type = RESOURCE_TYPES.DEVICE) =>
  dispatch => {
    dispatch(getMonitorDashboardsStart());
    return getDashboardsV1API(deviceTypeName, deviceId, type)
      .then(response => {
        dispatch(getMonitorDashboardsSuccess(response || []));
        return response;
      })
      .catch(error => {
        dispatch(getMonitorDashboardsFailure(error));
        throw error;
      });
  };

/**
 * Fetch hierarchy dashboards
 */
const getMonitorHierarchyDashboardsStart = (hierarchyId, hierarchyType) => ({
  type: GET_MONITOR_HIERARCHY_DASHBOARD,
  hierarchyId,
  hierarchyType,
});

const getMonitorHierarchyDashboardsFailure = (payload, hierarchyId, hierarchyType) => ({
  type: GET_MONITOR_HIERARCHY_DASHBOARD_FAILURE,
  payload,
  hierarchyId,
  hierarchyType,
});

const getMonitorHierarchyDashboardsSuccess = (payload, hierarchyId, hierarchyType) => ({
  type: GET_MONITOR_HIERARCHY_DASHBOARD_SUCCESS,
  payload,
  hierarchyId,
  hierarchyType,
});

export const fetchMonitorHierarchyDashboards = (path, hierarchyId, hierarchyType) => dispatch => {
  dispatch(getMonitorHierarchyDashboardsStart(hierarchyId, hierarchyType));
  return getHierarchyDashboardsAPI(path)
    .then(response => {
      dispatch(getMonitorHierarchyDashboardsSuccess(response || [], hierarchyId, hierarchyType));
      return response;
    })
    .catch(error => {
      dispatch(getMonitorHierarchyDashboardsFailure(error, hierarchyId, hierarchyType));
      throw error;
    });
};

// -------------
// PERSIST_HIERARCHY_LIST
// ------------

const setHierarchyList = storeData => ({
  type: PERSIST_HIERARCHY_LIST,
  payload: storeData,
});

export const persistHierarchyList =
  ({ listData, expandedId, expanded }) =>
  dispatch =>
    dispatch(setHierarchyList({ listData, expandedId, expanded }));

export const cleanHierarchyList = () => ({
  type: CLEAN_PERSIST_HIERARCHY_LIST,
});

/**
 * Fetch asset data
 */
const getAssetsStart = () => ({
  type: GET_MONITOR_ASSETS,
});

const getAssetsFailure = payload => ({
  type: GET_MONITOR_ASSETS_FAILURE,
  payload,
});

const getAssetsSuccess = payload => ({
  type: GET_MONITOR_ASSETS_SUCCESS,
  payload,
});

export const fetchAssets =
  ({ filters, limit, bookmark }) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getAssetsStart());
    const userEmailId = dataCache.get('currentUser')?.id;

    return getAssetResources({
      userEmailId,
      filters,
      limit,
      bookmark,
    })
      .then(responseJson =>
        filterUserResourcesToObject(responseJson, dataCache, RESOURCE_TYPES.ASSET)
      )
      .then(response => {
        dispatch(getAssetsSuccess(response?.results || []));

        return response;
      })
      .catch(error => {
        dispatch(getAssetsFailure(error));
        throw error;
      });
  };

/**
 * Fetch locations data
 */
const getLocationsStart = () => ({
  type: GET_MONITOR_LOCATIONS,
});

const getLocationsFailure = payload => ({
  type: GET_MONITOR_LOCATIONS_FAILURE,
  payload,
});

const getLocationsSuccess = payload => ({
  type: GET_MONITOR_LOCATIONS_SUCCESS,
  payload,
});
export const fetchLocations =
  ({ filters, limit, bookmark }) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getLocationsStart());
    const userEmailId = dataCache.get('currentUser')?.id;

    return getLocationResources({
      userEmailId,
      filters,
      limit,
      bookmark,
    })
      .then(responseJson =>
        filterUserResourcesToObject(responseJson, dataCache, RESOURCE_TYPES.LOCATION)
      )
      .then(response => {
        dispatch(getLocationsSuccess(response?.results || []));
        return response;
      })
      .catch(error => {
        dispatch(getLocationsFailure(error));
        throw error;
      });
  };
